/**
 * Sector operations — Construction Member Inspections section (R39 B9.b2).
 * Mobile-first inspection queue for phone width (375 px).
 * Displays pour & pre-concealment cards in INSPECTION_REQUESTED assigned to caller.
 * Online-only PASS/FAIL actions with server-computed permission flags.
 */

import React, { useEffect, useMemo, useState, useId } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { AppDialog, Button, Select, StatusPill, TextInput } from '../../Common'
import type { SelectOption } from '../../Common'
import { listConProjects } from '../../../services/conProjectsService'
import {
  listConPourCards,
  getConPourCard,
  recordConInspectionOutcome,
} from '../../../services/conQualityService'
import { getRecordPhotoUrl } from '../../../services/opsRecordPhotosService'
import { createOpId } from '../../../services/helper/opId'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import type {
  ConProject,
  OpsRecord,
  ConPourCardBlock,
  ConPourChecklistItem,
} from '../../../types/ops'
import styles from './MemberInspectionsPhoneSection.module.css'

export interface MemberInspectionsPhoneSectionProps {
  workspaceId: string
  callerUid: string
  scope: {
    ready: boolean
    restricted: boolean
    allowedPlaceIds?: ReadonlySet<string>
  }
}

const unwrapVal = (v: unknown): string =>
  v != null && typeof v === 'object' && 'target' in v
    ? String((v as { target: { value?: unknown } }).target.value ?? '')
    : String(v ?? '')

function ChecklistPhotoThumbnails({
  paths,
}: {
  paths?: string[]
}): React.ReactElement | null {
  const [photoUrls, setPhotoUrls] = useState<string[]>([])

  useEffect(() => {
    if (!paths || paths.length === 0) {
      return
    }
    let active = true
    void Promise.resolve().then(async () => {
      if (!active) return
      const urls: string[] = []
      for (const p of paths ?? []) {
        try {
          const u = await getRecordPhotoUrl(p)
          if (u) urls.push(u)
        } catch {
          // ignore photo load failure in thumbnail list
        }
      }
      if (active) setPhotoUrls(urls)
    })
    return () => {
      active = false
    }
  }, [paths])

  if (photoUrls.length === 0) return null

  return (
    <div className={styles.photoRow}>
      {photoUrls.map((url, i) => (
        <img
          key={url}
          src={url}
          alt={`Inspection item photo ${i + 1}`}
          className={styles.photoThumbnail}
          data-testid={`inspection-photo-thumbnail-${i}`}
        />
      ))}
    </div>
  )
}

export function MemberInspectionsPhoneSection({
  workspaceId,
  callerUid,
  scope,
}: MemberInspectionsPhoneSectionProps): React.ReactElement {
  const { t } = useNls()
  const passNotesId = useId()
  const failNotesId = useId()

  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Projects
  const [projects, setProjects] = useState<ConProject[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [projectsLoading, setProjectsLoading] = useState<boolean>(true)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!workspaceId || !scope.ready) return
    let active = true

    async function loadProjects() {
      setProjectsLoading(true)
      try {
        const res = await listConProjects({ workspaceId })
        if (active) {
          setProjects(res.projects || [])
        }
      } catch (err) {
        if (active) setErrorMsg(getApiErrorMessage(err))
      } finally {
        if (active) setProjectsLoading(false)
      }
    }

    void loadProjects()
    return () => {
      active = false
    }
  }, [workspaceId, scope.ready])

  const readableProjects = useMemo(() => {
    if (!scope.restricted || !scope.allowedPlaceIds) return projects
    return projects.filter((p) => scope.allowedPlaceIds?.has(p.placeId))
  }, [projects, scope.restricted, scope.allowedPlaceIds])

  const effectiveProjectId = useMemo(() => {
    if (selectedProjectId && readableProjects.some((p) => p.id === selectedProjectId)) {
      return selectedProjectId
    }
    return readableProjects[0]?.id || ''
  }, [readableProjects, selectedProjectId])

  // Cards List
  const [cards, setCards] = useState<Array<OpsRecord & { pour?: ConPourCardBlock }>>([])
  const [cardsLoading, setCardsLoading] = useState<boolean>(false)
  const [refreshNonce, setRefreshNonce] = useState<number>(0)

  useEffect(() => {
    if (!workspaceId || !effectiveProjectId) {
      return
    }
    let active = true

    void Promise.resolve().then(async () => {
      if (!active) return
      setCardsLoading(true)
      setErrorMsg(null)
      try {
        const res = await listConPourCards({
          workspaceId,
          projectId: effectiveProjectId,
        })
        if (active) {
          setCards(res.records || [])
        }
      } catch (err) {
        if (active) setErrorMsg(getApiErrorMessage(err))
      } finally {
        if (active) setCardsLoading(false)
      }
    })

    return () => {
      active = false
    }
  }, [workspaceId, effectiveProjectId, refreshNonce])

  // Filter for my cards in INSPECTION_REQUESTED
  const myRequestedCards = useMemo(() => {
    return cards.filter((c) => {
      const pour = c.pour
      if (!pour) return false
      return pour.holdState === 'INSPECTION_REQUESTED' && pour.inspectorUid === callerUid
    })
  }, [cards, callerUid])

  // Selected card detail
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [detailCard, setDetailCard] = useState<OpsRecord | null>(null)
  const [detailPour, setDetailPour] = useState<ConPourCardBlock | null>(null)
  const [canRecordOutcome, setCanRecordOutcome] = useState<boolean>(false)
  const [detailLoading, setDetailLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!workspaceId || !selectedCardId) {
      return
    }
    let active = true

    void Promise.resolve().then(async () => {
      if (!active) return
      setDetailLoading(true)
      setErrorMsg(null)
      setCanRecordOutcome(false)
      setDetailCard(null)
      setDetailPour(null)
      try {
        const res = await getConPourCard({
          workspaceId,
          cardId: selectedCardId ?? '',
        })
        if (active) {
          setDetailCard(res.card)
          setDetailPour(res.pour)
          setCanRecordOutcome(Boolean(res.canRecordOutcome))
        }
      } catch (err) {
        if (active) {
          setErrorMsg(getApiErrorMessage(err))
          setCanRecordOutcome(false)
          setDetailCard(null)
          setDetailPour(null)
        }
      } finally {
        if (active) setDetailLoading(false)
      }
    })

    return () => {
      active = false
    }
  }, [workspaceId, selectedCardId, refreshNonce])

  // Dialogs
  const [passDialogOpen, setPassDialogOpen] = useState<boolean>(false)
  const [passNotes, setPassNotes] = useState<string>('')
  const [failDialogOpen, setFailDialogOpen] = useState<boolean>(false)
  const [failNotes, setFailNotes] = useState<string>('')
  const [actionBusy, setActionBusy] = useState<boolean>(false)

  const handlePassSubmit = async () => {
    const requestId = detailPour?.inspectionRequestId
    if (!requestId || !isOnline) return
    setActionBusy(true)
    setErrorMsg(null)
    try {
      await recordConInspectionOutcome({
        workspaceId,
        requestId,
        outcome: 'PASS',
        notes: passNotes.trim() || null,
        opId: createOpId('insp_pass'),
      })
      setPassDialogOpen(false)
      setPassNotes('')
      setSelectedCardId(null)
      setRefreshNonce((n) => n + 1)
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  const handleFailSubmit = async () => {
    const requestId = detailPour?.inspectionRequestId
    if (!requestId || failNotes.trim().length < 3 || !isOnline) return
    setActionBusy(true)
    setErrorMsg(null)
    try {
      await recordConInspectionOutcome({
        workspaceId,
        requestId,
        outcome: 'FAIL',
        notes: failNotes.trim(),
        opId: createOpId('insp_fail'),
      })
      setFailDialogOpen(false)
      setFailNotes('')
      setSelectedCardId(null)
      setRefreshNonce((n) => n + 1)
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  const projectOptions: SelectOption<string>[] = useMemo(() => {
    return readableProjects.map((p) => ({
      value: p.id,
      label: `${p.code} — ${p.name}`,
    }))
  }, [readableProjects])

  return (
    <section
      className={styles.section}
      data-testid="member-inspections-section"
      aria-labelledby="member-inspections-title"
    >
      <div className={styles.sectionHeader}>
        <div className={styles.titleGroup}>
          <h2 id="member-inspections-title" className={styles.sectionTitle}>
            {t('NLSMemberOpsInspectionsTitle')}
          </h2>
          <p className={styles.sectionDesc}>
            {t('NLSMemberOpsInspectionsSubtitle')}
          </p>
        </div>
      </div>

      {!isOnline && (
        <div className={styles.offlineBanner} role="status">
          {t('NLSMemberOpsOfflineNotice')}
        </div>
      )}

      {errorMsg && (
        <div className={styles.errorBanner} role="alert">
          {errorMsg}
        </div>
      )}

      {/* Project Picker */}
      <div className={styles.pickerRow}>
        <span className={styles.pickerLabel}>
          {t('NLSMemberOpsDprProjectPickerLabel')}
        </span>
        <div data-testid="member-inspections-project-select">
          <Select
            ariaLabel={t('NLSMemberOpsDprProjectPickerLabel')}
            value={effectiveProjectId}
            onChange={(val) => {
              setSelectedProjectId(unwrapVal(val))
              setSelectedCardId(null)
            }}
            options={projectOptions}
            disabled={projectsLoading || readableProjects.length === 0}
          />
        </div>
      </div>

      {/* Cards List */}
      <div className={styles.listContainer}>
        {cardsLoading ? (
          <div className={styles.emptyState}>{t('NLSCommonStatusLoading')}</div>
        ) : myRequestedCards.length === 0 ? (
          <div className={styles.emptyState}>
            {t('NLSMemberOpsInspectionsEmptyQueue')}
          </div>
        ) : (
          myRequestedCards.map((c) => {
            const isSelected = c.id === selectedCardId
            const element = c.pour?.element || c.id
            const kind = c.pour?.kind || 'POUR'
            const plannedText = c.pour?.plannedAt
              ? new Date(c.pour.plannedAt).toLocaleDateString()
              : ''

            return (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                className={styles.inspectionCard}
                onClick={() => setSelectedCardId(isSelected ? null : (c.id || ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedCardId(isSelected ? null : (c.id || ''))
                  }
                }}
                data-testid={`member-inspection-card-${c.id}`}
              >
                <div className={styles.inspectionCardHeader}>
                  <h3 className={styles.elementTitle}>{element}</h3>
                  <StatusPill tone="warning">
                    {c.pour?.holdState ?? 'INSPECTION_REQUESTED'}
                  </StatusPill>
                </div>
                <div className={styles.cardMetaRow}>
                  <span>{kind}</span>
                  <span>{plannedText}</span>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Card Details Drawer / Inline Card */}
      {selectedCardId && (
        <div className={styles.detailsContainer} data-testid="member-inspection-details">
          {detailLoading ? (
            <div className={styles.emptyState}>{t('NLSCommonStatusLoading')}</div>
          ) : detailPour && detailCard ? (
            <>
              <div className={styles.detailsHeader}>
                <div>
                  <h3 className={styles.elementTitle}>{detailPour.element}</h3>
                  <div className={styles.detailsMeta}>
                    <span>
                      {t('NLSMemberOpsInspectionsKindLabel')}: {detailPour.kind}
                    </span>
                    <span>
                      {t('NLSMemberOpsInspectionsPlannedLabel')}:{' '}
                      {new Date(detailPour.plannedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <StatusPill tone="warning">{detailPour.holdState}</StatusPill>
              </div>

              {/* Checklist */}
              <div className={styles.checklistSection}>
                <h4 className={styles.checklistTitle}>
                  {t('NLSMemberOpsInspectionsChecklistTitle')}
                </h4>
                {detailPour.checklist.length === 0 ? (
                  <p className={styles.checklistItemNote}>
                    {t('NLSMemberOpsInspectionsNoChecklist')}
                  </p>
                ) : (
                  detailPour.checklist.map((item: ConPourChecklistItem) => {
                    const tone =
                      item.result === 'OK'
                        ? 'success'
                        : item.result === 'NOT_OK'
                          ? 'danger'
                          : 'neutral'
                    return (
                      <div key={item.itemKey} className={styles.checklistItem}>
                        <div className={styles.checklistItemHeader}>
                          <span className={styles.checklistItemLabel}>
                            {item.label}
                          </span>
                          <StatusPill tone={tone}>{item.result}</StatusPill>
                        </div>
                        {item.note && (
                          <p className={styles.checklistItemNote}>{item.note}</p>
                        )}
                        <ChecklistPhotoThumbnails
                          paths={item.photoPaths}
                        />
                      </div>
                    )
                  })
                )}
              </div>

              {/* Action Buttons: PASS and FAIL */}
              {canRecordOutcome && (
                <div className={styles.actionsRow}>
                  <Button
                    variant="danger"
                    onClick={() => setFailDialogOpen(true)}
                    disabled={!isOnline || actionBusy}
                    data-testid="member-inspection-btn-fail"
                  >
                    {t('NLSMemberOpsInspectionsFailButton')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setPassDialogOpen(true)}
                    disabled={!isOnline || actionBusy}
                    data-testid="member-inspection-btn-pass"
                  >
                    {t('NLSMemberOpsInspectionsPassButton')}
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* PASS Dialog */}
      <AppDialog
        open={passDialogOpen}
        title={t('NLSMemberOpsInspectionsPassDialogTitle')}
        onDismiss={() => setPassDialogOpen(false)}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => setPassDialogOpen(false),
            disabled: actionBusy,
          },
          {
            label: t('NLSMemberOpsInspectionsConfirmPass'),
            variant: 'primary',
            onClick: handlePassSubmit,
            disabled: actionBusy || !isOnline,
            testId: 'member-inspection-btn-confirm-pass',
          },
        ]}
      >
        <div className={styles.dialogForm}>
          <div className={styles.dialogField}>
            <label htmlFor={passNotesId} className={styles.dialogLabel}>
              {t('NLSMemberOpsInspectionsNotesLabel')}
            </label>
            <TextInput
              id={passNotesId}
              value={passNotes}
              onChange={(e) => setPassNotes(unwrapVal(e))}
              placeholder={t('NLSMemberOpsInspectionsNotesPlaceholder')}
            />
          </div>
        </div>
      </AppDialog>

      {/* FAIL Dialog */}
      <AppDialog
        open={failDialogOpen}
        title={t('NLSMemberOpsInspectionsFailDialogTitle')}
        onDismiss={() => setFailDialogOpen(false)}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => setFailDialogOpen(false),
            disabled: actionBusy,
          },
          {
            label: t('NLSMemberOpsInspectionsConfirmFail'),
            variant: 'danger',
            onClick: handleFailSubmit,
            disabled: actionBusy || failNotes.trim().length < 3 || !isOnline,
            testId: 'member-inspection-btn-confirm-fail',
          },
        ]}
      >
        <div className={styles.dialogForm}>
          <div className={styles.ncrNotice}>
            {t('NLSMemberOpsInspectionsNcrNotice')}
          </div>
          <div className={styles.dialogField}>
            <label htmlFor={failNotesId} className={styles.dialogLabel}>
              {t('NLSMemberOpsInspectionsReasonLabel')}
            </label>
            <TextInput
              id={failNotesId}
              data-testid="member-inspection-fail-notes-input"
              value={failNotes}
              onChange={(e) => setFailNotes(unwrapVal(e))}
              placeholder={t('NLSMemberOpsInspectionsReasonPlaceholder')}
            />
          </div>
        </div>
      </AppDialog>
    </section>
  )
}
